<template>
  <div v-if="active && !loading" class="modal is-active">
    <div @click="close" class="modal-background"></div>
    <aside class="modal-card">
      <section class="modalSection className">
        <div class="my-4">
          <h2 class="section-heading">{{ $t("teacherRegistration.chooseClassName")}}</h2>
          <p class="section-subHeading">{{ $t("teacherRegistration.chooseClassNameMessage")}}</p>
        </div>
        <div class="input-container">
          <input ref="className" class="classInput"
                 :type="'text'" :name="'className'"
                 :disabled="waiting"
                 :value="className"
                 v-model="className"
                 :placeholder="$t('teacherRegistration.enterClassName')"
                 :center="true"
                 :autocomplete="'off'"
                 data-lpignore="true" />
         <div v-if="error" class="help has-text-danger error">{{error}}</div>
        </div>
      </section>
      <section class="modalSection classImage">
        <div class="mb-4">
          <h2 class="section-heading">{{ $t("teacherRegistration.chooseClassImage")}}</h2>
          <p class="section-subHeading">{{ $t("teacherRegistration.chooseClassImageMessage")}}</p>
        </div>
        <div class="classImage-upload">
          <image-upload ref="classImageUpload" :isProfileImage="true" :showAvatarPicker="false" :image="classImage" :callback="setClassImage" proxyWidth="200" proxyHeight="200"></image-upload>
        </div>
      </section>
      <section class="modalSection classCode">
        <div class="mb-4">
          <h2 class="section-heading">{{ $t("teacherRegistration.chooseClassCode")}}</h2>
          <p class="section-subHeading">{{ $t("teacherRegistration.chooseClassCodeMessage")}}</p>
        </div>
        <div v-if="!updateClass" ref="classCode" class="classCodeContainer">
          <p class="classCodeText">{{createdClassCode}}</p>
        </div>
        <div v-if="updateClass" class="classCodeContainer">
          <p class="classCodeText" ref="classCode">{{ classCode }}</p>
        </div>
        <p @click="copyText()" class="copyText is-primary mt-2">Copy Class Code</p>
        <p v-show="showCopyTextMessage" class="copyTextMessage">copied to clipboard</p>
      </section>
      <footer class="modalSection footer">
        <button  class="button is-uppercase is-outlined is-primary cancelButton" @click="close()" :disabled="waiting">{{ $t('cancel') }}</button>
        <button  v-if="updateClass" class="button is-primary is-uppercase" @click="updateClassImageAndClassInfo()" :disabled="waiting">{{ $t('teacherRegistration.save') }}</button>
        <button  v-else-if="!updateClass" class="button is-primary is-uppercase" @click="saveNewClassImageAndClassInfo()" :disabled="waiting">{{ $t('teacherRegistration.save') }}</button>
      </footer>
    </aside>
  </div>
</template>

<script>
import ModalCustom from '@/components/Modals/Custom'
import ImageUpload from '@/components/Inputs/ImageUpload'
import componentState from '@/mixins/component-state'
import debounce from 'lodash/debounce'
import auth from '@/libs/auth'
import i18n from '@/i18n'
export default {
  components: {
    ModalCustom,
    i18n,
    ImageUpload
  },
  props: ['selectedClass', 'updateClass', 'createClass'],
  data: function () {
    return {
      error: '',
      active: false,
      teacherCode: '',
      className: '',
      waiting: false,
      loading: false,
      classCode: '',
      classIdToUpdateName: '',
      classImage: '',
      signupClassInstance: !(this.selectedClass || this.updateClass || this.createClass),
      showCopyTextMessage: false
    }
  },
  mixins: [componentState, auth],
  computed: {
    group: function () {
      return this.$store.getters.teacherGroup
    },
    currentUser: function () {
      return this.$store.getters.currentUser
    },
    createdClassCode: function () {
      return this.$store.getters.classCode
    }
  },
  mounted: function () {
    this.refreshTeacherModal()
    if (!this.signupClassInstance) {
      this.active = true
    }

    if (this.selectedClass && !this.createClass) {
      this.classCode = this.selectedClass.code
      this.className = this.selectedClass.name
      this.classImage = this.selectedClass.image
    } else {
      this.createClassCode()
    }
  },
  watch: {
    active: function (activate) {
      if (activate) {
        this._callback('lockScrolling', false)
        this.refreshTeacherModal()
      } else {
        this._callback('unlockScrolling', true)
      }
    }
  },
  methods: {
    refreshTeacherModal: function () {
      this.loading = true
      if (this.active && this.signupClassInstance) {
        // short circuit this modal if it's already completed (on refresh)
        this.$store.dispatch('getClassesForTeacher', this.currentUser.id).then(() => {
          if (this.$store.getters.teacherClasses.length > 0) {
            this.$nextTick(() => {
              this.close()
            })
          }
        }, (error) => {
          console.error(error)
        })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.loading = false
      }
    },
    showModal: function () {
      this.active = true
    },
    close: debounce(function () {
      this.active = false
      this.$emit('closeTeacherCodeModal')
    }),
    copyText: function () {
      navigator.clipboard.writeText(this.$refs.classCode.innerText)
      this.showCopyTextMessage = true
      setTimeout(() => {
        this.showCopyTextMessage = false
      }, 5000)
    },
    createClassCode: function () {
      this.$store.dispatch('createCode').then((classCode) => {
        this.classCode = classCode
      }, (error) => {
        this.error = error
      })
    },
    updateClassImageAndClassInfo: function () {
      this.$refs.classImageUpload.uploadImage('classImage', `${this.currentUser.uuid}_${Date.now()}`, this.classImage).then((classImageURL) => {
        this.updateClassInfo(classImageURL)
      })
    },
    saveNewClassImageAndClassInfo: function () {
      this.$refs.classImageUpload.uploadImage('classImage', `${this.currentUser.uuid}_${Date.now()}`, this.classImage).then((classImageURL) => {
        this.createNewClass(classImageURL)
      })
    },
    setClassImage: function (classImage) {
      this.classImage = classImage
    },
    createNewClass: function (classImageURL) {
      this.waiting = true
      if (!this.className) {
        this.error = 'Please enter class name to continue.'
        this.waiting = false
      } else {
        const params = {
          className: this.className,
          userId: this.currentUser.id,
          classImage: classImageURL,
          classCode: this.classCode
        }
        this.$store.dispatch('createNewGroup', params).then(() => {
          this.close()
          this.waiting = false
        }, (error) => {
          this.error = error
        })
      }
    },
    updateClassInfo: function (classImageURL) {
      this.waiting = true
      this.classIdToUpdateName = this.selectedClass.id
      if (!this.className) {
        this.error = 'Please enter class name to continue.'
        this.waiting = false
      } else {
        const params = {
          className: this.className,
          userId: this.currentUser.id,
          classId: this.classIdToUpdateName,
          classImage: classImageURL
        }
        this.$store.dispatch('updateClassName', params).then((response) => {
          this.close()
          this.waiting = false
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

$modal-body-color: white;
$border-color: #DCDCDC;

.modal-card {
  background: $modal-body-color;
  border-radius: 2rem;
  text-align: center;
  font-family: cera-pro;
  @include mobile {
    max-width: 98vw;
  }
}

::v-deep .modal-header {
  text-align: left;
}

.modalSection {
  width: 70%;
  margin: 0 auto;
  padding: 1.6rem 0 0 0;
  font-size: 1rem;
  @include mobile {
    width: 90%;
  }
}

.section-heading {
  font-size: 1.6rem;
  font-weight: 800;
}

.section-subHeading {
  font-size: 0.9rem;
  font-weight: 400;
}

// HEADER
.header {
  font-size: 1.5rem;
  font-weight: 400;
  border-bottom: 1px solid $border-color;
  h1 {
    padding: 1.3rem 0;
    @include mobile {
      font-size: 1.3rem;
    }
  }
}

// CLASS NAME
.input-container {
  width: 100%;
  margin: 1rem 0 0 0;
}

.classInput {
  padding: 0.75rem 1rem;
  width: inherit;
  border: 1.5px solid $border-color;
  border-radius: 0.2rem;
  @include mobile {
    padding-bottom: 1rem;
  }
}

// FOOTER
.footer {
  background: $modal-body-color;
  padding: 0.5rem 0 2.6rem 0rem;
  text-align: right;
  .button {
    font-size: 0.8rem;
    border-radius: 0;
  }
  .cancelButton {
    border: 1px solid black;
  }
}

// CLASS CODE
.classCodeContainer {
  border: solid $border-color 2px;
  padding: 0.4rem;
  border-radius: 0.5rem;
}

.classCodeText {
  font-size: 3.8rem;
  color: #005bab;
}

.copyText {
  text-align: left;
  cursor: pointer;
  color: #00549e;
  &:hover {
    color: #004078;
  }
}

.copyTextMessage {
  color: green;
  font-size: 0.75rem;
  text-align: left;
  position: absolute;
}
</style>
